import CursorUtils from './cursor-utils'
import CursorActions from './cursor-actions'
import CursorAnimations from './cursor-animations'

/**
 * CursorController - Controls individual cursor behavior
 *
 * This controller is dynamically created by CursorManager
 * and handles all cursor-specific logic including:
 * - Positioning and movement
 * - Instruction execution
 * - Animation management
 * - Action handling
 */
export default class CursorController {
  constructor(element, config, manager) {
    this.element = element
    this.config = config
    this.manager = manager
    this.isInitialized = false
    this.isDestroyed = false

    // Current state
    this.currentPosition = { ...config.position }
    this.targetPosition = { ...config.position }
    this.isMoving = false
    this.isIdleMoving = false // Track if currently doing idle animation
    this.isExecuting = false

    // Instruction queue
    this.instructionQueue = [...(config.instructions || [])]
    this.currentInstruction = null

    // Animation state
    this.animationFrame = null
    this.moveStartTime = null
    this.moveDuration = 0
    this.moveStartPosition = null
    this.moveEndPosition = null
    this.movePath = null

    // Action state
    this.actionElements = new Map() // Track elements we've acted on
    this.selectedElements = new Set() // Track selected elements

    // Resize handling state
    this.lastTargetElement = null // Track the last target element for resize recalculation
    this.lastTargetOptions = null // Track the last target options (anchor, offset)
    this.lastRelativeOffset = null // Track relative offset for spawn positioning
    this.idleBasePosition = null // Track base position when idling
    this.hasMovedFromSpawn = false // Track if cursor has moved away from spawn position
    this.positionUpdateEnabled = true // Allow disabling position updates during transitions

    // Anchor element tracking for resize
    this.anchorElement = null // Element to use as anchor point during resize
    this.anchorOptions = null // Options for anchor positioning
    this.anchorRelativePosition = null // Position relative to anchor

    // Utilities
    this.utils = new CursorUtils(this)
    this.actions = new CursorActions(this)
    this.animations = new CursorAnimations(this)
  }

  initialize() {
    if (this.isInitialized) return

    // Connect to Stimulus
    this.connect()

    // Initial setup
    this.setupInitialState()

    // Start processing instructions (with optional initial delay)
    if (this.config.initialDelay) {
      setTimeout(() => {
        this.startInstructionProcessing()
      }, this.config.initialDelay)
    } else {
      this.startInstructionProcessing()
    }

    this.isInitialized = true
  }

  connect() {
    // Animate spawn
    this.animateSpawn()
  }

  disconnect() {
    // Clean up
    this.cleanup()
  }

  /**
   * Setup initial state
   */
  setupInitialState() {
    // Set initial position without scroll adjustment
    this.updateElementPosition(this.currentPosition, true)

    // Set name
    const nameElement = this.element.querySelector('.cursor__name')
    if (nameElement) {
      nameElement.textContent = this.config.name
    }

    // Set color class
    this.element.classList.add(`color__${this.config.color}`)

    // Initially hidden for spawn animation
    this.element.style.opacity = '0'
    this.element.style.transform = 'scale(0.8)'
  }

  /**
   * Animate cursor spawn
   */
  animateSpawn() {
    // Add spawn animation
    this.element.style.transition = 'opacity 0.3s ease, transform 0.3s ease'

    requestAnimationFrame(() => {
      this.element.style.opacity = '1'
      this.element.style.transform = 'scale(1)'
    })
  }

  /**
   * Animate cursor despawn
   */
  animateDespawn() {
    return new Promise((resolve) => {
      this.element.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
      this.element.style.opacity = '0'
      this.element.style.transform = 'scale(0.8)'

      setTimeout(() => {
        resolve()
      }, 300)
    })
  }

  /**
   * Add instructions to the queue
   * @param {Array} instructions - Array of instruction objects
   */
  addInstructions(instructions) {
    this.instructionQueue.push(...instructions)

    // Start processing if not already doing so
    if (!this.isExecuting) {
      this.startInstructionProcessing()
    }
  }

  /**
   * Start processing instruction queue
   */
  async startInstructionProcessing() {
    if (this.isExecuting || this.instructionQueue.length === 0) return

    this.isExecuting = true

    // Store original instructions if looping is enabled
    const originalInstructions = this.config.loop ? [...this.instructionQueue] : null

    while (this.instructionQueue.length > 0 && !this.isDestroyed) {
      const instruction = this.instructionQueue.shift()
      this.currentInstruction = instruction

      try {
        await this.executeInstruction(instruction)
      } catch (error) {
        // Error executing instruction - silently continue
      }

      this.currentInstruction = null
    }

    // If looping is enabled, reset the queue and wait for the loop delay
    if (this.config.loop && originalInstructions && !this.isDestroyed) {
      // Wait for the specified loop delay or default to 1000ms
      const loopDelay = this.config.loopDelay || 1000
      await this.utils.wait(loopDelay)

      // Reset the queue with the original instructions
      this.instructionQueue = [...originalInstructions]

      // Continue processing
      this.isExecuting = false
      this.startInstructionProcessing()
      return
    }

    this.isExecuting = false
  }

  /**
   * Execute a single instruction
   * @param {Object} instruction - The instruction to execute
   */
  async executeInstruction(instruction) {
    // Disable position updates during instruction execution
    this.positionUpdateEnabled = false

    try {
      // If we're currently idling, stop it before executing the next instruction
      if (this.isIdling && instruction.type !== 'idle') {
        this.stopIdling()
      }

      // Apply delay if specified
      if (instruction.delay) {
        await this.utils.wait(instruction.delay)
      }

      switch (instruction.type) {
        case 'spawn':
          await this.executeSpawn(instruction)
          break

        case 'moveTo':
          await this.executeMoveTo(instruction)
          break

        case 'action':
          await this.executeAction(instruction)
          break

        case 'despawn':
          await this.executeDespawn(instruction)
          break

        case 'idle':
          await this.executeIdle(instruction)
          break

        default:
        // Unknown instruction type - skip
      }
    } finally {
      // Re-enable position updates after a small delay to prevent interference
      setTimeout(() => {
        this.positionUpdateEnabled = true
      }, 100)
    }
  }

  /**
   * Execute spawn instruction
   */
  async executeSpawn(instruction) {
    // Handle different position types
    if (instruction.position) {
      let targetPosition = instruction.position

      // Convert percentage-based position to absolute pixels
      if (typeof targetPosition.x === 'string' || typeof targetPosition.y === 'string') {
        targetPosition = this.utils.convertPercentagePosition(targetPosition)
      }

      this.currentPosition = { ...targetPosition }
      this.updateElementPosition(this.currentPosition)
    }
  }

  /**
   * Execute moveTo instruction
   */
  async executeMoveTo(instruction) {
    let targetPosition
    let targetElement = null

    // Mark that we've moved from spawn position
    this.hasMovedFromSpawn = true

    // Handle relative positioning
    if (instruction.relative) {
      targetPosition = this.utils.calculateRelativePosition(this.currentPosition, instruction.relative)

      if (!targetPosition) {
        return
      }

      // Clear spawn-relative positioning since we're moving to a new relative position
      if (this.config.relativeToTarget) {
        this.config.relativeToTarget = null
        this.config.relativeOffset = null
      }

      // Also clear anchor since we're moving relatively (not to a specific element)
      this.anchorElement = null
      this.anchorOptions = null
      this.anchorRelativePosition = null
    }
    // Handle element targeting
    else if (instruction.target && typeof instruction.target === 'string') {
      // Try to find the element with retries
      for (let attempt = 0; attempt < 3; attempt++) {
        targetElement = this.utils.getElement(instruction.target)

        // Check if element is ready for interaction (displayed and has valid position)
        // Allow hidden elements if instruction specifically allows it (for reveal actions, etc.)
        if (targetElement && (this.utils.isElementReady(targetElement) || instruction.allowHidden)) {
          break
        } else if (targetElement && !this.utils.isElementReady(targetElement)) {
          targetElement = null // Reset to null so we can retry or skip
        }

        // Wait a bit and retry if element not found or not ready
        if (attempt < 2) {
          await this.utils.wait(100)
        }
      }

      // Move to element if found
      if (targetElement) {
        // Support enhanced positioning options
        const options = {
          anchor: instruction.anchor,
          offset: instruction.offset,
          allowHidden: instruction.allowHidden,
        }
        targetPosition = this.utils.getElementPosition(instruction.target, options)
        // Calculate target position

        // Store target information for resize handling
        this.lastTargetElement = instruction.target
        this.lastTargetOptions = options
        this.lastRelativeOffset = null

        // If we're moving to a target element, it becomes our new anchor
        this.anchorElement = instruction.target
        this.anchorOptions = options
      }

      if (!targetPosition) {
        // Could not find ready target element after retries
        return
      }
    }
    // Handle absolute positioning (with percentage support)
    else if (instruction.position) {
      targetPosition = { ...instruction.position }

      // Convert percentage-based position to absolute pixels
      if (typeof targetPosition.x === 'string' || typeof targetPosition.y === 'string') {
        targetPosition = this.utils.convertPercentagePosition(targetPosition)
      }
    } else {
      return
    }

    // Validate target position
    if (!targetPosition || typeof targetPosition.x !== 'number' || typeof targetPosition.y !== 'number') {
      return
    }

    // Calculate movement
    const distance = this.utils.calculateDistance(this.currentPosition, targetPosition)
    const duration = instruction.duration || this.utils.calculateMoveDuration(distance)

    try {
      // Generate movement path
      const path = this.animations.generateMovementPath(
        this.currentPosition,
        targetPosition,
        instruction.pathType || 'bezier'
      )

      // Execute movement
      await this.animateMovement(path, duration)
    } catch (error) {
      // Error executing instruction - silently continue
    }
  }

  /**
   * Execute action instruction
   */
  async executeAction(instruction) {
    const target = instruction.target
    const action = instruction.action

    if (!target || !action) {
      return
    }

    // Get target element
    const element = this.utils.getElement(target)
    if (!element) {
      return
    }

    // Check if element is ready for interaction
    // Allow hidden elements if instruction specifically allows it (for reveal actions, etc.)
    if (!this.utils.isElementReady(element) && !instruction.allowHidden) {
      return
    }

    // Execute action
    await this.actions.executeAction(action, element, instruction)
  }

  /**
   * Execute despawn instruction
   */
  async executeDespawn(instruction) {
    await this.animateDespawn()
    this.destroy()
  }

  /**
   * Execute idle instruction - cursor stays in place with realistic human-like movements
   * @param {Object} instruction - Instruction object
   */
  async executeIdle(instruction) {
    const {
      duration = 2000,
      radius = 5,
      variation = 0.3,
      loops = 2,
      points = 30,
      className = 'collab__entity--idle',
      infinite = false,
      // New parameters for realistic idle
      minPause = 800, // Minimum pause between movements
      maxPause = 2500, // Maximum pause between movements
      minMoveDuration = 300, // Minimum movement duration
      maxMoveDuration = 800, // Maximum movement duration
      fidgetChance = 0.3, // Chance of making a fidget movement (0-1)
    } = instruction

    // Add idle class if specified
    if (className) {
      this.element.classList.add(className)
    }

    // Set idle flag for both infinite and finite idles
    this.isIdling = true

    // Store the base position for idle animation (for resize recalculation)
    this.idleBasePosition = { ...this.currentPosition }

    // If we have an anchor element, calculate and store our position relative to it
    if (this.anchorElement) {
      const anchorPos = this.utils.getElementPosition(this.anchorElement, this.anchorOptions)
      if (anchorPos) {
        this.anchorRelativePosition = {
          x: this.currentPosition.x - anchorPos.x,
          y: this.currentPosition.y - anchorPos.y,
        }
      }
    }

    // For infinite idle, we need to keep generating realistic movements
    if (infinite) {
      // Create a promise that will resolve when idle is stopped
      return new Promise((resolve) => {
        // Store the resolve function to call it later when stopping idle
        this.stopIdling = () => {
          this.isIdling = false
          if (className) {
            this.element.classList.remove(className)
          }
          resolve()
        }

        // Start the realistic idle animation loop
        this.realisticIdleLoop({
          radius,
          variation,
          minPause,
          maxPause,
          minMoveDuration,
          maxMoveDuration,
          fidgetChance,
        })
      })
    }
    // For finite idle, run for the specified duration
    else {
      // Run realistic idle for the specified duration
      await this.realisticIdleLoop({
        radius,
        variation,
        minPause,
        maxPause,
        minMoveDuration,
        maxMoveDuration,
        fidgetChance,
        totalDuration: duration,
      })

      // Clean up after finite idle
      this.isIdling = false
      if (className) {
        this.element.classList.remove(className)
      }
    }
  }

  /**
   * Run realistic human-like idle animation
   * @param {Object} options - Options for idle behavior
   */
  async realisticIdleLoop(options) {
    const {
      radius = 5,
      variation = 0.3,
      minPause = 800,
      maxPause = 2500,
      minMoveDuration = 300,
      maxMoveDuration = 800,
      fidgetChance = 0.3,
      totalDuration = null,
    } = options

    const startTime = Date.now()
    let centerPosition = { ...this.currentPosition }

    // Continue until isIdling is set to false or duration is reached
    while (this.isIdling && !this.isDestroyed) {
      // Update center position if it changed due to resize
      if (this.idleBasePosition) {
        centerPosition = { ...this.idleBasePosition }
      }

      // Check if we've reached the total duration (for finite idle)
      if (totalDuration && Date.now() - startTime >= totalDuration) {
        break
      }

      // Decide if we should make a movement or just pause
      const shouldMove = Math.random() < fidgetChance

      if (shouldMove) {
        // Generate a small movement near the center position
        const angle = Math.random() * Math.PI * 2
        const distance = Math.random() * radius * (1 + variation * (Math.random() - 0.5))

        const targetPosition = {
          x: centerPosition.x + Math.cos(angle) * distance,
          y: centerPosition.y + Math.sin(angle) * distance,
        }

        // Generate a natural movement path to the target
        const path = this.animations.generateMovementPath(this.currentPosition, targetPosition, 'bezier')

        // Move with a random duration
        const moveDuration = minMoveDuration + Math.random() * (maxMoveDuration - minMoveDuration)

        // Mark as idle moving and animate the movement
        this.isIdleMoving = true
        await this.animateMovement(path, moveDuration)
        this.isIdleMoving = false
      }

      // Pause for a random amount of time (simulating looking/thinking)
      const pauseDuration = minPause + Math.random() * (maxPause - minPause)
      await this.utils.wait(pauseDuration)

      // If we've moved too far from center, occasionally drift back
      const distanceFromCenter = this.utils.calculateDistance(this.currentPosition, centerPosition)
      if (distanceFromCenter > radius * 1.5 && Math.random() < 0.7) {
        // Drift back towards center
        const driftTarget = {
          x: centerPosition.x + (Math.random() - 0.5) * radius * 0.5,
          y: centerPosition.y + (Math.random() - 0.5) * radius * 0.5,
        }

        const driftPath = this.animations.generateMovementPath(this.currentPosition, driftTarget, 'bezier')

        const driftDuration = minMoveDuration + Math.random() * (maxMoveDuration - minMoveDuration)
        this.isIdleMoving = true
        await this.animateMovement(driftPath, driftDuration)
        this.isIdleMoving = false
      }
    }
  }

  /**
   * Stop the current idle animation if one is running
   */
  stopIdling() {
    if (this.isIdling && this.stopIdling) {
      this.stopIdling()
      this.isIdling = false
      this.isIdleMoving = false
      this.stopIdling = null
    }
  }

  /**
   * Animate movement along a path
   * @param {Array} path - Array of position objects
   * @param {number} duration - Animation duration in ms
   */
  async animateMovement(path, duration) {
    return new Promise((resolve) => {
      // Safety check for path validity
      if (!path || !Array.isArray(path) || path.length === 0) {
        resolve()
        return
      }

      // Ensure target exists before starting animation
      const firstPoint = path[0]
      const lastPoint = path[path.length - 1]

      if (
        !firstPoint ||
        !lastPoint ||
        typeof firstPoint.x !== 'number' ||
        typeof firstPoint.y !== 'number' ||
        typeof lastPoint.x !== 'number' ||
        typeof lastPoint.y !== 'number'
      ) {
        resolve()
        return
      }

      this.isMoving = true
      this.moveStartTime = performance.now()
      this.moveDuration = duration || 500 // Default duration if none provided
      this.movePath = path

      // Add moving class
      this.element.classList.add(this.movingClass || 'collab__entity--moving')

      const animate = (currentTime) => {
        if (this.isDestroyed) {
          resolve()
          return
        }

        const elapsed = currentTime - this.moveStartTime
        const progress = Math.min(elapsed / this.moveDuration, 1)

        try {
          // Get current position from path with error handling
          const position = this.animations.getPositionFromPath(path, progress)

          // Verify position is valid
          if (position && typeof position.x === 'number' && typeof position.y === 'number') {
            this.currentPosition = position
            this.updateElementPosition(position)
          }
        } catch (error) {}

        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(animate)
        } else {
          this.isMoving = false
          this.element.classList.remove(this.movingClass || 'collab__entity--moving')
          resolve()
        }
      }

      this.animationFrame = requestAnimationFrame(animate)
    })
  }

  /**
   * Update the cursor element position
   * @param {Object} position - {x, y} coordinates
   * @param {boolean} isInitial - Whether this is initial positioning (don't adjust for scroll)
   */
  updateElementPosition(position, isInitial = false) {
    // With absolute positioning, we use document coordinates directly
    // No need to adjust for scroll as the browser handles this automatically
    this.element.style.left = `${position.x}px`
    this.element.style.top = `${position.y}px`
  }

  /**
   * Update position (called on resize/scroll)
   */
  updatePosition() {
    // Check if position updates are enabled
    if (!this.positionUpdateEnabled) return

    // Don't interfere with any movements or instruction execution
    if (this.isMoving || this.isExecuting) return

    // Don't update if we're between instructions (prevents jump during transitions)
    if (this.currentInstruction) return

    // For anchored cursors, position is always relative to their container element - no updates needed
    if (this.config.anchored && this.config.anchoredToTarget) {
      return // Anchored cursors don't need position updates on resize/scroll
    }

    // With absolute positioning, we only need to update when the target element moves
    // No need to update on every scroll event

    // First priority: If we're idling with an anchor, maintain relative position to anchor
    if (this.isIdling && this.anchorElement && this.anchorRelativePosition) {
      const anchorPos = this.utils.getElementPosition(this.anchorElement, this.anchorOptions)
      if (anchorPos) {
        // Calculate new position based on anchor + relative offset
        const newPosition = {
          x: anchorPos.x + this.anchorRelativePosition.x,
          y: anchorPos.y + this.anchorRelativePosition.y,
        }

        this.currentPosition = newPosition
        this.idleBasePosition = { ...newPosition }
      }
    }
    // Second priority: If we have a target element, recalculate position based on it
    else if (this.lastTargetElement) {
      // Check if target element is still ready for interaction
      if (this.utils.isElementReady(this.lastTargetElement)) {
        const newPosition = this.utils.getElementPosition(this.lastTargetElement, this.lastTargetOptions)
        if (newPosition) {
          // Update current position to the new target position
          this.currentPosition = newPosition

          // If we're idling, update the base position too
          if (this.isIdling) {
            this.idleBasePosition = { ...newPosition }
          }
        }
      } else {
        // Target element is no longer ready, clear the tracking
        // Target element no longer ready during resize, clearing target tracking
        this.lastTargetElement = null
        this.lastTargetOptions = null
      }
    }
    // If we were spawned relative to a target AND haven't moved away, recalculate
    else if (this.config.relativeToTarget && !this.hasMovedFromSpawn) {
      const targetElement = document.querySelector(this.config.relativeToTarget)
      if (targetElement && this.utils.isElementReady(targetElement)) {
        const rect = targetElement.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

        // Get positioning context offset
        const contextOffset = this.utils.getPositioningContextOffset()

        // Get element center position in document coordinates
        const elementPosition = {
          x: rect.left + scrollLeft + rect.width / 2 - contextOffset.x,
          y: rect.top + scrollTop + rect.height / 2 - contextOffset.y,
        }

        // Apply relative offset
        const newPosition = {
          x: elementPosition.x + (this.config.relativeOffset?.x || 0),
          y: elementPosition.y + (this.config.relativeOffset?.y || 0),
        }

        this.currentPosition = newPosition

        // If we're idling, update the base position too
        if (this.isIdling) {
          this.idleBasePosition = { ...newPosition }
        }
      } else if (targetElement && !this.utils.isElementReady(targetElement)) {
      }
    }

    this.updateElementPosition(this.currentPosition)
  }

  /**
   * Clean up cursor
   */
  cleanup() {
    // Stop any idle animation
    if (this.isIdling) {
      this.stopIdling()
    }

    // Cancel any ongoing animations
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
    }

    // Clean up action elements
    this.actions.cleanup()

    // Clear selected elements
    this.selectedElements.clear()

    // Clear instruction queue
    this.instructionQueue = []
    this.currentInstruction = null

    // Clear resize tracking state
    this.lastTargetElement = null
    this.lastTargetOptions = null
    this.lastRelativeOffset = null
    this.idleBasePosition = null
    this.hasMovedFromSpawn = false
    this.positionUpdateEnabled = true

    // Clear anchor tracking
    this.anchorElement = null
    this.anchorOptions = null
    this.anchorRelativePosition = null

    this.isExecuting = false
    this.isMoving = false
    this.isIdleMoving = false
  }

  /**
   * Destroy cursor
   */
  destroy() {
    if (this.isDestroyed) return

    this.isDestroyed = true
    this.cleanup()

    // Remove from DOM
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element)
    }

    // Notify manager
    this.manager.removeCursor(this.config.id)
  }

  /**
   * Debug logging
   * @param {...any} args - Arguments to log
   */
  debug(...args) {
    if (this.manager.debugValue) {
    }
  }

  /**
   * Get current cursor state
   * @returns {Object} - Current state
   */
  getState() {
    return {
      id: this.config.id,
      name: this.config.name,
      color: this.config.color,
      position: { ...this.currentPosition },
      isMoving: this.isMoving,
      isExecuting: this.isExecuting,
      instructionQueue: [...this.instructionQueue],
      currentInstruction: this.currentInstruction,
    }
  }
}
